import { useTranslation } from 'react-i18next';
import { ChevronRightIcon } from '@heroicons/react/20/solid';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { InvoiceDiscrepancy } from 'services/invoiceDiscrepancies/types';
import { cn } from 'support/helpers/cn/cn';
import { Button } from 'components/Form/Button/Button';
import { InfoPopoverBody, InfoPopoverFooter } from 'components/InfoPopover/InfoPopover';

import { AllowanceDiscrepancyView } from './AllowanceDiscrepancyView';
import { ChargeDiscrepancyView } from './ChargeDiscrepancyView';
import { PriceDiscrepancyView } from './PriceDiscrepancyView';
import { QuantityDiscrepancyView } from './QuantityDiscrepancyView';
import { TaxDiscrepancyView } from './TaxDiscrepancyView';

type DiscrepancyContentProps = {
  error: InvoiceDiscrepancy;
};

export const WithErrorsContent = ({ error, numberOfErrors }: { error: InvoiceDiscrepancy; numberOfErrors: number }) => {
  const { t } = useTranslation();

  const { service } = useNavigationContext<InvoiceDiscrepancy>();
  const showNextDiscrepancyButton = numberOfErrors > 1;

  return (
    <>
      <InfoPopoverBody>
        <div className="flex justify-between gap-2">
          <h1 className="text-sm font-bold text-red-700">
            {t(`invoiceVerification:detailsPage.discrepanciesModal.title.${error.type}`)}
          </h1>
        </div>
        <div className="pt-3 text-sm text-gray-700">
          <DiscrepancyContent error={error} />
        </div>
      </InfoPopoverBody>
      <InfoPopoverFooter>
        <div></div> {/* This empty div is to align the buttons to the right */}
        <Button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={service.moveToNextItem}
          variant="minimal-danger"
          RightIcon={ChevronRightIcon}
          size="extra-small"
          className={cn(showNextDiscrepancyButton ? 'visible' : 'invisible')}
          analyticsId="invoiceVerification:detailsPage.discrepanciesModal.nextDiscrepancy"
        >
          {t('invoiceVerification:detailsPage.discrepanciesModal.nextDiscrepancyButton')}
        </Button>
      </InfoPopoverFooter>
    </>
  );
};

const DiscrepancyContent = ({ error }: DiscrepancyContentProps) => {
  switch (error.type) {
    case 'prices':
      return (
        <PriceDiscrepancyView
          data={error.data}
          relatedTransactions={error.relatedTransactions}
          currency={error.currency}
        />
      );
    case 'taxes':
      return <TaxDiscrepancyView data={error.data} relatedTransactions={error.relatedTransactions} />;
    case 'quantities':
      return <QuantityDiscrepancyView data={error.data} />;
    case 'charges':
      return <ChargeDiscrepancyView data={error.data} currency={error.currency} />;
    case 'allowances':
      return <AllowanceDiscrepancyView data={error.data} currency={error.currency} />;
    default:
      return null;
  }
};

/*
 * i18n keys
 * t('invoiceVerification:detailsPage.discrepanciesModal.title.quantities')
 * t('invoiceVerification:detailsPage.discrepanciesModal.title.prices')
 * t('invoiceVerification:detailsPage.discrepanciesModal.title.taxes')
 * t('invoiceVerification:detailsPage.discrepanciesModal.title.charges')
 * t('invoiceVerification:detailsPage.discrepanciesModal.title.allowances')
 */
